/**
 * Migration utility to create missing system transaction types
 *
 * Problem: Users who signed up before initializeSystemTypes() was implemented
 * are missing transaction types in the transaction_types Firestore collection.
 *
 * This script:
 * 1. Checks which SYSTEM_TRANSACTION_TYPES exist in Firestore
 * 2. Creates any missing types for the current user
 * 3. Preserves existing types and their settings
 *
 * Usage (browser console):
 *   await window.migrateMissingTransactionTypes()
 */

import { collection, query, where, getDocs, addDoc, Timestamp } from 'firebase/firestore';
import { db, auth } from '@/lib/firebase';
import { SYSTEM_TRANSACTION_TYPES } from '@/types/transaction-type';

/**
 * Get current logged-in user ID
 */
function getCurrentUserId(): string {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('No user logged in. Please login first.');
  }
  return user.uid;
}

/**
 * Get existing transaction types for user
 */
async function getExistingTypes(userId: string): Promise<Set<string>> {
  console.log('[migrateMissingTransactionTypes] Fetching existing types for userId:', userId);

  const typesRef = collection(db, 'transaction_types');
  const q = query(typesRef, where('userId', '==', userId));
  const snapshot = await getDocs(q);

  const existingNames = new Set<string>();
  snapshot.forEach((doc) => {
    const data = doc.data();
    existingNames.add(data.name);
  });

  console.log('[migrateMissingTransactionTypes] Existing types:', Array.from(existingNames));
  return existingNames;
}

/**
 * Create missing transaction types for user
 *
 * @param userId - Optional user ID. If not provided, uses currently logged-in user.
 * @param dryRun - If true, only shows what would be created without actually creating
 */
export async function migrateMissingTransactionTypes(
  userId?: string,
  dryRun: boolean = false
): Promise<{
  success: boolean;
  existing: string[];
  created: string[];
  errors: string[];
}> {
  const targetUserId = userId || getCurrentUserId();
  console.log('[migrateMissingTransactionTypes] Starting migration (dryRun:', dryRun, ')');
  console.log('[migrateMissingTransactionTypes] Target userId:', targetUserId);

  try {
    // Get existing types
    const existingTypes = await getExistingTypes(targetUserId);

    // Determine missing types
    const missingTypes = SYSTEM_TRANSACTION_TYPES.filter(
      (systemType) => !existingTypes.has(systemType.name)
    );

    console.log('[migrateMissingTransactionTypes] System types:', SYSTEM_TRANSACTION_TYPES.length);
    console.log('[migrateMissingTransactionTypes] Existing:', existingTypes.size);
    console.log('[migrateMissingTransactionTypes] Missing:', missingTypes.length);

    if (missingTypes.length === 0) {
      console.log('[migrateMissingTransactionTypes] ✅ No missing types found. All system types exist.');
      return {
        success: true,
        existing: Array.from(existingTypes),
        created: [],
        errors: [],
      };
    }

    console.log('[migrateMissingTransactionTypes] Missing types to create:');
    missingTypes.forEach((type, index) => {
      console.log(`  ${index + 1}. ${type.name} (displayOrder: ${type.displayOrder})`);
    });

    if (dryRun) {
      console.log('[migrateMissingTransactionTypes] DRY RUN - No types will be created');
      return {
        success: true,
        existing: Array.from(existingTypes),
        created: missingTypes.map((t) => t.name),
        errors: [],
      };
    }

    // Create missing types
    const typesRef = collection(db, 'transaction_types');
    const createdAt = Timestamp.now().toDate().toISOString();
    const created: string[] = [];
    const errors: string[] = [];

    for (const systemType of missingTypes) {
      try {
        await addDoc(typesRef, {
          userId: targetUserId,
          name: systemType.name,
          displayOrder: systemType.displayOrder,
          isSystemType: true,
          isActive: true,
          createdAt,
        });
        created.push(systemType.name);
        console.log('[migrateMissingTransactionTypes] ✅ Created:', systemType.name);
      } catch (error) {
        const errorMsg = `Failed to create ${systemType.name}: ${error}`;
        console.error('[migrateMissingTransactionTypes]', errorMsg);
        errors.push(errorMsg);
      }
    }

    console.log('[migrateMissingTransactionTypes] Migration complete');
    console.log('[migrateMissingTransactionTypes] Created:', created.length);
    console.log('[migrateMissingTransactionTypes] Errors:', errors.length);

    return {
      success: errors.length === 0,
      existing: Array.from(existingTypes),
      created,
      errors,
    };
  } catch (error) {
    console.error('[migrateMissingTransactionTypes] Migration failed:', error);
    throw error;
  }
}
